import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AveragePercentageCalculator = () => {
  const [entries, setEntries] = useState([{
    id: Date.now(),
    value: '',
    percentage: ''
  }]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (id, field, value) => {
    setEntries(prev => prev.map(entry => 
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
    setError('');
  };

  const addEntry = () => {
    setEntries(prev => [
      ...prev,
      {
        id: Date.now(),
        value: '',
        percentage: ''
      }
    ]);
  };

  const removeEntry = (id) => {
    const updatedEntries = entries.filter(entry => entry.id !== id);
    if (updatedEntries.length > 0) {
      setEntries(updatedEntries);
    }
  };

  const calculateAverage = () => {
    // Validate all entries
    for (const entry of entries) {
      if (!entry.value || !entry.percentage) {
        setError('Please fill in all fields');
        return;
      }
      if (isNaN(entry.value) || isNaN(entry.percentage)) {
        setError('Please enter valid numbers');
        return;
      }
      if (parseFloat(entry.percentage) < 0) {
        setError('Percentages cannot be negative');
        return;
      }
    }

    const totalPercentage = entries.reduce((sum, entry) => 
      sum + parseFloat(entry.percentage), 0
    );

    if (totalPercentage === 0) {
      setError('Total percentage cannot be zero');
      return;
    }

    const weightedSum = entries.reduce((sum, entry) => 
      sum + (parseFloat(entry.value) * parseFloat(entry.percentage)), 0
    );

    const average = (weightedSum / totalPercentage).toFixed(2);

    // Create visualization data
    const labels = entries.map((_, index) => `Entry ${index + 1}`);
    const values = entries.map(entry => parseFloat(entry.percentage));
    const contributions = entries.map(entry => 
      (parseFloat(entry.value) * parseFloat(entry.percentage)).toFixed(2)
    );

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Percentage Weight',
        data: values,
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };

    setResult({
      average,
      entries,
      chartData,
      contributions,
      totalPercentage
    });
  };

  const exampleData = {
    entries: [
      { value: '90', percentage: '40' },
      { value: '80', percentage: '30' },
      { value: '70', percentage: '30' }
    ],
    average: '83.00'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-blue-100 to-teal-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Average Percentage Calculator | Weighted Average Tool</title>
          <meta name="description" content="Calculate weighted average percentages for grades, investments, or any scenario requiring weighted calculations." />
          <meta name="keywords" content="average percentage calculator, weighted average, percentage calculator, grade calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-blue-600 mr-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800">Average Percentage Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate weighted average percentages for grades, investments, or any scenario requiring weighted calculations. This tool helps determine the average value based on different weights or percentages.</p>
          </div>

          <div className="space-y-4 mb-6">
            {entries.map(entry => (
              <div key={entry.id} className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Value:</label>
                  <input
                    type="text"
                    value={entry.value}
                    onChange={(e) => handleInputChange(entry.id, 'value', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter value (e.g., 85)"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Percentage (%):</label>
                  <input
                    type="text"
                    value={entry.percentage}
                    onChange={(e) => handleInputChange(entry.id, 'percentage', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter percentage (e.g., 30)"
                  />
                </div>
                <button
                  onClick={() => removeEntry(entry.id)}
                  className="py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={addEntry}
              className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Add Another Entry
            </button>
          </div>

          <button
            onClick={calculateAverage}
            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 mt-4"
          >
            Calculate Average
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Weighted Average Calculation</h3>
                  <p className="text-lg">Total Percentage: {result.totalPercentage.toFixed(2)}%</p>
                  <p className="text-lg font-bold mt-4">Weighted Average: {result.average}%</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Method: Weighted Average</p>
                  <p className="text-lg">Formula: (Σ(value × percentage)) / Σ(percentage)</p>
                </div>
              </div>

              {result.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={result.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Percentage Weight'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Entries'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Percentage Distribution'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Breakdown</h3>
                <div className="space-y-4">
                  {result.entries.map((entry, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <p className="text-gray-700">
                        <strong>Entry {index + 1}:</strong> {entry.value} × {entry.percentage}% = {result.contributions[index]}%
                      </p>
                    </div>
                  ))}
                  <div className="p-4 bg-white rounded-lg shadow-inner font-bold">
                    <p>Total: {result.weightedSum.toFixed(2)} / {result.totalPercentage.toFixed(2)} = {result.average}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for academic grades:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Exam 1: {exampleData.entries[0].value}% (weight: {exampleData.entries[0].percentage}%)</li>
                <li>Exam 2: {exampleData.entries[1].value}% (weight: {exampleData.entries[1].percentage}%)</li>
                <li>Homework: {exampleData.entries[2].value}% (weight: {exampleData.entries[2].percentage}%)</li>
                <li>Weighted Average: {exampleData.average}%</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Multiply each value by its percentage:
                  <ul className="list-disc pl-6">
                    <li>90 × 0.4 = 36</li>
                    <li>80 × 0.3 = 24</li>
                    <li>70 × 0.3 = 21</li>
                  </ul>
                </li>
                <li>Sum the results: 36 + 24 + 21 = 81</li>
                <li>The weighted average is 81%</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Average Percentage Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Data:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input values (e.g., grades, investment returns)</li>
                  <li>Input corresponding percentages (weights)</li>
                </ul>
              </li>
              <li><strong>Add Entries:</strong> Click "Add Another Entry" for multiple data points</li>
              <li><strong>Calculate:</strong> Click the calculate button to get results</li>
              <li><strong>Review Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>View the weighted average percentage</li>
                  <li>Check the calculation breakdown</li>
                  <li>Visualize the percentage distribution</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use for grade calculations with different weightings</li>
                <li>Calculate portfolio returns with different investment sizes</li>
                <li>Works for any scenario requiring weighted averages</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Weighted Averages <span className="text-blue-600">📊</span></h2>
          <div className="text-gray-600">
            <p>
              Weighted averages consider both the value and its relative importance (weight). They're more accurate than simple averages when:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Different Importance:</strong> Values have different significance (e.g., exams vs homework)</li>
              <li><strong>Resource Allocation:</strong> Investments with different sizes</li>
              <li><strong>Performance Metrics:</strong> Business KPIs with different priorities</li>
              <li><strong>Grading Systems:</strong> Courses with different weightings</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Consistency:</strong> Use the same units for all values</li>
              <li><strong>Total Percentage:</strong> Ensure percentages add up to 100% for complete weightings</li>
              <li><strong>Verification:</strong> Double-check your inputs before calculating</li>
              <li><strong>Documentation:</strong> Keep records of your calculations for future reference</li>
            </ul>
            <p className="italic mt-4">
              Note: Weighted average gives more importance to values with higher percentages, providing a more accurate representation of the overall situation.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AveragePercentageCalculator;